import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'

import { ArrowForwardIcon } from '@/components/icons'
import ReservationExpiryChip from '@/components/checkout/order-summary/reservation-expiry-chip'
import CheckoutEndTime from '@/components/checkout/order-summary/end-time'
import { formatCzkAmount } from '@/components/checkout/order-summary/utils'
import ReservationDuration from '@/components/reservation-duration'
import type { CheckoutSummaryProps } from '@/types/reservation-checkout'

export default function CheckoutOrderSummaryDesktop({
    garageName,
    garageImageUrl,
    owner,
    beginsAtDateLabel,
    beginsAtTimeLabel,
    endsAtDateLabel,
    endsAtTimeLabel,
    reservationDays,
    reservationHours,
    finalPrice,
    originalPrice,
    discountAmount,
    expiredAt,
    minimumStay,
}: CheckoutSummaryProps) {
    const { t, i18n } = useTranslation()
    const hasDiscount = typeof originalPrice === 'number' && typeof discountAmount === 'number' && discountAmount > 0
    const locale = i18n.resolvedLanguage || i18n.language

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            fontSize: '0.875rem',
            background: 'white',
            borderRadius: 10,
            border: '1px solid var(--joy-palette-neutral-100)',
        }}>
            <Box sx={{
                display: 'flex',
                gap: 2,
                paddingX: 2,
                paddingY: 1.625,
                alignItems: 'flex-start',
            }}>
                {garageImageUrl ? (
                    <Box
                        component="img"
                        src={garageImageUrl}
                        alt={garageName}
                        sx={{
                            width: 80,
                            height: 48,
                            objectFit: 'cover',
                            borderRadius: 8,
                            flexShrink: 0,
                        }}
                    />
                ) : null}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    gap: 0.25,
                }}>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                    }}>
                        <Typography level="body-lg" sx={{
                            fontWeight: 600,
                            flexGrow: 1,
                        }}>
                            {garageName}
                        </Typography>
                        <ReservationExpiryChip expiredAt={expiredAt} size="md" />
                    </Box>
                    {owner ? (
                        <Box>
                            <Typography level="body-xs" textColor="neutral.500">
                                {owner.name}
                                {owner.ic ? ` (${t('checkout.orderSummary.identificationNumber')}: ${owner.ic})` : null}
                            </Typography>
                            <Typography level="body-xs" textColor="neutral.500">
                                {t(owner.vat_payer
                                    ? 'checkout.orderSummary.vatPayer'
                                    : 'checkout.orderSummary.notVatPayer')}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
            </Box>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto auto',
                columnGap: 2,
                rowGap: 0.75,
                paddingX: 2,
                paddingY: 1.625,
                borderTop: '1px solid var(--joy-palette-neutral-100)',
                alignItems: 'center',
            }}>
                <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                    <Typography level="body-sm" sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        mb: 0.75,
                    }}>
                        {t('checkout.orderSummary.start')}
                    </Typography>
                    <Typography level="body-md">
                        <b>{beginsAtDateLabel}</b>, {beginsAtTimeLabel}
                    </Typography>
                </Box>
                <Box sx={{ gridColumn: 2, gridRow: 1 }}>
                    <ArrowForwardIcon color="#BDBDBD" width={20} height={20} />
                </Box>
                <CheckoutEndTime endsAtDateLabel={endsAtDateLabel} endsAtTimeLabel={endsAtTimeLabel} minimumStay={minimumStay} desktop />
            </Box>
            <Box sx={{
                paddingX: 2,
                paddingY: 1.625,
                borderTop: '1px solid var(--joy-palette-neutral-100)',
            }}>
                <Typography level="body-sm" sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    mb: 0.75,
                }}>
                    {t('checkout.orderSummary.duration')}
                </Typography>
                <Typography level="body-md" sx={{
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                }}>
                    <ReservationDuration
                        reservationDays={reservationDays}
                        reservationHours={reservationHours}
                    />
                </Typography>
            </Box>
            <Box sx={{
                paddingX: 2,
                paddingY: 1.5,
                borderTop: '1px solid var(--joy-palette-neutral-100)',
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) max-content',
                columnGap: 2,
                rowGap: 1,
                alignItems: 'baseline',
            }}>
                {hasDiscount ? (
                    <>
                        <Typography level="body-sm" sx={{
                            textAlign: 'right',
                        }}>
                            {t('checkout.orderSummary.originalPrice')}
                        </Typography>
                        <Typography level="body-sm" sx={{
                            textDecoration: 'line-through',
                            textAlign: 'right',
                        }}>
                            {formatCzkAmount(originalPrice, locale)}
                        </Typography>
                        <Typography level="body-sm" sx={{
                            textAlign: 'right',
                        }}>
                            {t('checkout.orderSummary.discount')}
                        </Typography>
                        <Typography level="body-sm" sx={{
                            textAlign: 'right',
                        }}>
                            -{formatCzkAmount(discountAmount, locale)}
                        </Typography>
                    </>
                ) : null}
                <Typography level="body-lg" sx={{
                    textAlign: 'right',
                }}>
                    {t('checkout.orderSummary.total')}
                </Typography>
                <Typography level="body-lg" sx={{
                    textAlign: 'right',
                    color: 'customGreen.main',
                }}>
                    <b>{formatCzkAmount(finalPrice, locale)}</b>
                    {owner?.vat_payer ? ` ${t('checkout.orderSummary.vatIncluded')}` : null}
                </Typography>
            </Box>
        </Box>
    )
}
